import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PublicationHelper } from '../../../../helpers/publication.helper';
import { Member } from '../../../../models/clubs/member.model';
import { MentionMember } from '../../../../models/clubs/mention-member.model';
import { LinkifyPipe } from '../../../../pipes/linkify.pipe';
import { MentionMembersPipe } from '../../../../pipes/mention-members.pipe';
import { PublicationService } from '../../../../services/clubs/publication.service';
import { DocumentsService } from '../../../../services/dms/documents.service';
import { SignalrIntegration } from '../../../../shared/interfaces/SignalrIntegration';
import { ModalPublicationReactionDetailsComponent } from '../modal-publication-reactions-details/modal-publication-reactions-details';
import { ClubsStore } from './../../../../clubs/clubs.store';
import { regexPatterns } from './../../../../constants/regex-patterns';
import { ClubRoles } from './../../../../models/clubs/club-roles.model';
import { Publication } from './../../../../models/clubs/publication.model';
import { ReactionType } from './../../../../models/clubs/reaction-type.model';
import { Reaction } from './../../../../models/clubs/reaction.model';
import { MemberApiService } from './../../../../services/clubs/member.api.service';
import { ReactionTypeService } from './../../../../services/clubs/reaction-type.service';
import { AccountsService } from './../../../../services/identity/accounts.service';
import { PostsSignalrService } from './../../../../services/signalr/posts-signalr.service';
import { ModalPublicationPictureComponent } from './../modal-publication-picture/modal-publication-picture.component';

@Component({
    templateUrl: './base-post-card.component.html',
    providers: [MentionMembersPipe, LinkifyPipe],
})
export class BasePostCardComponent implements OnInit, OnDestroy, SignalrIntegration {
    private destroy$ = new Subject<void>();
    @Input() public isFromHomeDrawer = false;

    public areCommentsDisplayed = false;
    public canEditPost: boolean;
    public clubRoles = ClubRoles;
    public comments: Publication[];
    public currentUserReaction: string;
    @Input() public highlightedCommentId: string;
    public hoverOnAddCustomReaction = false;
    public hoverOnLike = false;
    @Input() public lastVisitDate: Date;
    public membersAssociatedToReactions: { member: Member; reaction: Reaction }[] = [];
    @ViewChild(ModalPublicationPictureComponent)
    public modalPublicationPictureComponent: ModalPublicationPictureComponent;
    @ViewChild(ModalPublicationReactionDetailsComponent)
    public modalPublicationReactionDetailsComponent: ModalPublicationReactionDetailsComponent;
    @Output()
    public onCommentCreated: EventEmitter<Publication> = new EventEmitter<Publication>();
    @Output()
    public onCommentDeleted: EventEmitter<void> = new EventEmitter<void>();
    public page = 1;
    @Input() public parentPublicationId: string;
    public peopleWrittingCommentCount = 0;
    public postIsSeen: boolean;
    public postOwnerRole: ClubRoles;
    @Input() public publication: Publication;
    @Output()
    public publicationEdited: EventEmitter<Publication> = new EventEmitter<Publication>();
    @Input() public publicationId: string;
    public reactionTypes: ReactionType[];
    public sanitizedContent: SafeHtml;
    public showEditPost = false;

    constructor(
        protected readonly accountService: AccountsService,
        protected readonly memberService: MemberApiService,
        protected publicationService: PublicationService,
        protected reactionTypeService: ReactionTypeService,
        readonly clubsStore: ClubsStore,
        public readonly publicationHelper: PublicationHelper,
        protected readonly postsSignalrService: PostsSignalrService,
        protected readonly documentService: DocumentsService,
        public readonly sanitizer: DomSanitizer,
        protected mentionPipe: MentionMembersPipe,
        protected linkifyPipe: LinkifyPipe,
    ) {}

    public ngOnInit(): void {

        if (!this.isFromHomeDrawer) {
            this.postsSignalrService.restartConnectionEvent$
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    if (this.postsSignalrService.isPostFocused(this.publication.id)) {
                        this.postsSignalrService.currentGroupsNames = [];
                        this.initSignalr();
                    }
                });
        }


        if (this.publication == undefined) {
            this.getPublication();
        } else {
            this.loadData();
        }

    }

    public ngOnDestroy(): void {
        if (this.hasSignalrFocus && this.accountService.isAuthenticated) {
            this.postsSignalrService.leaveGroup(this.publication.id, this.accountService.currentUser.userId);
        } else {
            this.postsSignalrService.stopConnection();
        }
        // signalr cleanup
        if (this.hasSignalrFocus && this.accountService.isAuthenticated) {
            this.postsSignalrService.leaveGroup(this.publication?.id, this.accountService.currentUser.userId);
        } else {
            this.postsSignalrService.stopConnection();
        }

        // complete local observables to stop all takeUntil pipes
        this.destroy$.next();
        this.destroy$.complete();
    }

    public set currentReaction(value: string) {
        if (this.currentUserReaction === undefined || this.currentUserReaction !== value) {
            this.currentUserReaction = value;
        } else {
            this.currentUserReaction = undefined;
        }
    }

    public get hasReaction(): boolean {
        for (const key in this.publication?.reactions) {
            if (this.publication.reactions[key] > 0) {
                return true;
            }
        }
        return false;
    }

    public get hasSignalrFocus(): boolean {
        return this.postsSignalrService.isPostFocused(this.publication.id);
    }

    public addOrRemoveReaction(typeReactionId: string) {
        if (!this.publication.disabled) {
            this.publicationService
                .editMemberReactionToPublication(this.publication.id, typeReactionId)
                .pipe(takeUntil(this.destroy$))
                .subscribe(async (editMemberReactionResult) => {
                    this.hoverOnAddCustomReaction = false;
                    this.hoverOnLike = false;
                    this.publication.reactions = editMemberReactionResult.publication.reactions;
                    this.currentReaction = typeReactionId;
                    await this.setFocusOnPost();
                    this.loadPublicationsReactions();

                    this.postsSignalrService.addOrRemoveReaction(this.publication.id, typeReactionId, editMemberReactionResult.previousReaction);
                });
        }
    }

    public addOrUpdateReactionLocally(reactionTypeId: string) {
        if (this.publication.reactions == null) this.publication.reactions = {};
        if (this.publication.reactions[reactionTypeId]) this.publication.reactions[reactionTypeId] += 1;
        else this.publication.reactions[reactionTypeId] = 1;
    }

    public defineCanEditPost(): boolean {
        return (
            !this.publication.disabled &&
            (this.publication.userId == this.accountService.currentUser.userId ||
                this.clubsStore.isAdminOfDisplayedClub ||
                this.clubsStore.isAnimatorOfDisplayedRoom)
        );
    }

    public defineCanReportPost(): boolean {
        return !this.publication.disabled && this.publication.userId !== this.accountService.currentUser.userId;
    }

    public disableHoverOnLike() {
        setTimeout(() => {
            this.hoverOnLike = false;
        }, 500);
    }

    public getComments() {
        this.publicationService.getPublicationCommentByPages(this.publication.id, this.page)
            .pipe(takeUntil(this.destroy$))
            .subscribe((results) => {
                if (this.page === 1) {
                    this.comments = [];
                }
                results.forEach((result) => {
                    this.comments.unshift(result);
                });
                this.page++;
            });
    }

    public getLinkInContent(): string {
        if (this.publication?.attachedDocumentId) {
            return null;
        }

        if (this.publication?.attachedUrl) {
            return this.publication.attachedUrl;
        }

        const contentSplited = this.publication?.content?.split(' ');
        if (contentSplited?.length > 0) {
            for (const word of contentSplited) {
                for (const wordSplitedByBreakLine of word.split('\n')) {
                    if (wordSplitedByBreakLine.match(regexPatterns.fullUrl)) {
                        return wordSplitedByBreakLine;
                    }
                }
            }
        } else if (this.publication?.content?.match(regexPatterns.fullUrl)) {
            return this.publication.content;
        }

        return null;
    }

    public getPostReactionsDetails() {
        if (!this.isFromHomeDrawer) {
            this.modalPublicationReactionDetailsComponent.showModalDialog();
        }
    }

    public getPublication() {
        this.publicationService.getPublicationById(this.publicationId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                this.publication = result;
                this.loadData();
            });
    }

    public getReactionTypeCode(reactionTypeId: string): string {
        return this.reactionTypes?.find((reactionType) => reactionType.id == reactionTypeId)?.code;
    }

    public async initSignalr() {
        await this.postsSignalrService.joinGroup(this.publication.id, this.accountService.currentUser.userId, this.publication.parentPublicationId);
        this.initSignalrListener();
    }

    public initSignalrListener() {
        this.postsSignalrService.addReactionListener(
            this.publication.id,
            (postId: string, reactionTypeId: string, previousReaction: Reaction = null) => {
                if (this.hasSignalrFocus && postId == this.publication.id) {
                    if (previousReaction != null) this.removeReactionLocally(previousReaction);

                    if (
                        previousReaction == null || //Create reaction case
                        previousReaction.reactionTypeId != reactionTypeId //Update reaction case
                    )
                        this.addOrUpdateReactionLocally(reactionTypeId);
                }
            },
        );

        this.postsSignalrService.addOnWrittingListener(this.publication.id, (postId: string, isWritting: boolean) => {
            if (this.hasSignalrFocus && postId == this.publication.id) {
                if (isWritting) {
                    this.peopleWrittingCommentCount += 1;
                } else if (this.peopleWrittingCommentCount > 0) {
                    this.peopleWrittingCommentCount -= 1;
                }

                if (this.peopleWrittingCommentCount > 0)
                    setTimeout(() => {
                        this.peopleWrittingCommentCount -= 1;
                    }, 120000);
            } else {
                this.peopleWrittingCommentCount = 0;
            }
        });

        this.postsSignalrService.addCommentListener(this.publication.id, (postId: string, comment: Publication) => {
            if (this.hasSignalrFocus && postId == this.publication.id) {
                this.onAddNewComment(comment);
                this.publication.commentsCount++;
            }
        });
    }

    public isCurrentMemberMentioned(): boolean {
        return this.publication.mentionMembers?.some((mention) => mention.userId == this.accountService.currentUser.userId);
    }

    public loadData() {
        this.sanitizedContent = this.sanitizeAndParseContent(this.publication.content, this.publication?.mentionMembers);
        this.canEditPost = this.defineCanEditPost();

        this.postOwnerRole = this.clubsStore.getMemberRoleByMemberId(this.publication.userId);

        this.areCommentsDisplayed = this.publication?.depth === 1 || false;

        this.reactionTypeService.getAllReactionTypes()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                this.reactionTypes = result;
            });

        if (this.publication.id != null && this.publication.comments != null) {
            this.comments = this.highlightedCommentId ? this.publication.comments : this.publication.comments.slice(-2);
        }

        if (this.publication.depth == 1) {
            this.postIsSeen = new Date(this.lastVisitDate) < new Date(this.publication.creationDate) ? false : true;
        }

        if (this.publication.hasJustBeenCreated) {
            this.setFocusOnPost();
        }


        this.loadPublicationsReactions();
    }

    public loadPublicationsReactions() {
        if (this.publication.reactions == null) return;

        //Get post reactions from API
        this.publicationService.getReactionsByPublicationId(this.publication.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((reactions) => {
                this.publication.reactionsDetails = reactions;

                this.publication.reactions = reactions.reduce((acc: { [key: string]: number }, reaction) => {
                    acc[reaction.reactionTypeId] = (acc[reaction.reactionTypeId] || 0) + 1;
                    return acc;
                }, {});

                const currentUserReaction = reactions.find(
                        (reaction) => reaction.memberId == this.accountService.currentUser.userId,
                    );
                this.currentUserReaction = currentUserReaction?.reactionTypeId;

                // Process reactions with members using data-driven approach
                this.processReactionsWithMembers(reactions);
            });
    }

    private processReactionsWithMembers(reactions: Reaction[]) {
        //Get all members ids associated to post reactions
        const reactionsMembersIds = reactions.map((reaction) => reaction.memberId);
        //Get only members who have a reaction
        const membersWithReactions = this.clubsStore.flatMembers.filter((flatMember) => reactionsMembersIds.includes(flatMember.id));

        //Check if we have all required members
        const hasAllMembers = reactionsMembersIds.every(memberId =>
            membersWithReactions.some(member => member.id === memberId)
        );

        if (hasAllMembers && membersWithReactions.length > 0) {
            // All members are available, process immediately
            this.associateReactionsWithMembers(reactions, membersWithReactions);
        } else {
            // Some members are missing, process with available ones anyway
            this.associateReactionsWithMembers(reactions, membersWithReactions);

            // If no members at all, try to load them
            if (membersWithReactions.length === 0 && this.clubsStore.flatMembers.length === 0) {
                this.handleMissingMembers(reactions, reactionsMembersIds, membersWithReactions);
            }
        }
    }

    private associateReactionsWithMembers(reactions: Reaction[], membersWithReactions: Member[]) {
        this.membersAssociatedToReactions = [];

        //Associate reaction to its complete member
        for (const reaction of reactions) {
            const associatedMember = membersWithReactions.find((member) => member.id == reaction.memberId);

            if (associatedMember) {
                this.membersAssociatedToReactions.push({
                    member: associatedMember,
                    reaction,
                });
            }
        }
    }

    private handleMissingMembers(reactions: Reaction[], reactionsMembersIds: string[], membersWithReactions: Member[]) {
        //Check if store has members loaded
        if (this.clubsStore.flatMembers.length > 0) {
            // Store has members but some are missing, process with available ones
            this.associateReactionsWithMembers(reactions, membersWithReactions);
        } else {
            // Store has no members, try to trigger member loading and process when ready
            this.clubsStore.loadMembers();
            // Wait for members to load then process
            const memberSubscription = this.clubsStore.membersLoadedEvent$.subscribe(() => {
                const allMembersWithReactions = this.clubsStore.flatMembers.filter((flatMember) =>
                    reactionsMembersIds.includes(flatMember.id)
                );
                this.associateReactionsWithMembers(reactions, allMembersWithReactions);
                memberSubscription.unsubscribe();
            });
        }
    }

    public onAddNewComment(newComment: Publication) {
        if (newComment?.id != null) {
            if (this.publicationHelper.isPublicationMaxDepth(this.publication)) this.onCommentCreated.emit(newComment);
            else {
                if (this.comments == null) this.comments = [];
                if (this.comments.find((comment) => comment.id == newComment.id) == null) {
                    this.comments.push(newComment);
                    this.areCommentsDisplayed = true;
                }
            }
        }
    }

    public onClickOnPicture() {
        this.modalPublicationPictureComponent.showModalDialog();
    }

    public onCommentDeletedFunction() {
        this.publication.commentsCount--;
    }

    public onPublicationDeleted() {
        if (this.publication.depth > 1) this.onCommentDeleted.emit();
        if (this.publication.attachedDocumentId) {
            this.documentService
                .deleteDocument({ documentId: this.publication.attachedDocumentId, clubId: this.clubsStore.displayedRoomId })
                .pipe(takeUntil(this.destroy$))
                .subscribe();
        }
        this.publication = null;
    }

    public onPublicationRepostedAsAnnouncement(publication: Publication) { }

    public onPublicationUpdated(publication: Publication) {
        this.publication.content = publication.content;
        this.sanitizedContent = this.sanitizeAndParseContent(this.publication.content, this.publication?.mentionMembers);
        this.publication.pictureAccessLink = null;
        setTimeout(() => {
            this.publication.pictureAccessLink = publication.pictureAccessLink;
        }, 500);

        this.publicationEdited.emit(this.publication);
    }

    public removeReactionLocally(previousReaction: Reaction) {
        this.publication.reactions[previousReaction.reactionTypeId] -= 1;
    }

    public sanitizeAndParseContent(content: string, mentionMembers: MentionMember[]): SafeHtml {
        if (!this.isHtmlContent(content)) {
            content = this.linkifyPipe.transform(content);
        }

        content = this.mentionPipe.transform(content, mentionMembers);

        return this.sanitizer.bypassSecurityTrustHtml(content);
    }

    public async setFocusOnPost() {
        if (!this.hasSignalrFocus) {
            if (this.publication.depth > 1)
                setTimeout(async () => {
                    await this.initSignalr();
                }, 2000);
            else await this.initSignalr();
        }
    }

    public toogleCommentsDisplay() {
        this.areCommentsDisplayed = !this.areCommentsDisplayed;
    }

    private isHtmlContent(text: string): boolean {
        const htmlRegex = /<\/?[a-z][\s\S]*>/i;
        return htmlRegex.test(text);
    }
}
